<template>
    <div v-show="!isContentReady" class='tep-loader-wrap-outer'>
        <div class='tep-loader-wrap-inner'>
            <div class="absolute top-1/2 left-[45%]"><dx-loader /></div>
        </div>
    </div>
    <div id='tep-tree-view-container' class='tep-scroll-container'>
        <local-tree-view
            id='tep-tree-view'
            ref='treeViewRef'
            :data-source='dataSource'
            :meta='treeViewMeta'
            @item-click="onItemClick"
            @item-collapsed='onItemCollapsed'
        >
            <template v-slot:toolbarItems>
                <div class='tep-tree-view-toolbar'>
                    <div class='tep-tree-view-toolbar-item' style="width: 48px">
                        <dx-button class="ai-button-base" hint="Обновить данные" @click="onRefresh">
                            <ai-refresh-base />
                        </dx-button>
                    </div>
                    <div class='tep-tree-view-toolbar-item'>
                        <ai-form-check-box
                            :meta="metaCheckboxKod"
                            :callbacks='{ userDataChanged: changeCheckboxKod }'
                        ></ai-form-check-box>
                    </div>
                    <div class='tep-tree-view-toolbar-item' style="flex: 1;">
                       <tep-tree-search
                           @openSearchItemTree="onOpenSearchItemTree"
                       ></tep-tree-search>
                    </div>
                </div>
            </template>
            <template #item="{ data }">
                <div :id='"tree_item_" + data?.kod' class='tep-tree-view-item' @contextmenu.prevent='e => e.preventDefault()'>
                    <tep-tree-context-menu
                        :target='"#tree_item_" + data?.kod'
                        :tree-item-data='data'
                        :move-enabled='checkBoxMoveValue'
                        :copied-item='copiedItem'
                        @expand-item='expandAllChildren'
                        @copy-kod='(item) => copiedItem = item'
                        @move-item='onMoveItem'
                        @create-item='onCreateItem'
                        @delete-item='onDeleteItem'
                        @get-open-el="onGetOpenEl"
                    ></tep-tree-context-menu>
                    <template v-if='loadingTreeItemList.findIndex(el => el === data?.kod) === -1'>
                        <img :src='`/infoenergo/assets/tep/tep-tree/kod_vid_${data?.kod_vid}.svg`' class='tep-tree-item-img' alt=''>
                    </template>
                    <template v-else>
                        <div class='tep-tree-item-img'>
                            <dx-load-indicator :height="20" :width="20" />
                        </div>
                    </template>
                    <span class='tep-tree-item-text'>{{checkBoxKodValue ? data?.name2 : data?.name }}</span>
                </div>
            </template>
        </local-tree-view>
    </div>
</template>

<script>
export default {
    name: 'view-tep-tree'
};
</script>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, provide, ref } from 'vue';
import { DxButton } from 'devextreme-vue';
import { DxLoadIndicator } from 'devextreme-vue/load-indicator';
import DataSource from 'devextreme/data/data_source';
import CustomStore from 'devextreme/data/custom_store';
import DxLoader from '@/components/loader/dx-loader.vue';
import { getCookie } from '@/utils/cookies';
import store from './store/tep-tree-store';
import AiFormCheckBox from '@/components/card-component/ai-form-check-box.vue';
import LocalTreeView from '@/components/local-tree-view.vue';
import TepTreeContextMenu from '@/views/tep/tep-grid/tep-tree-context-menu.vue';
import TepTreeSearch from '@/views/tep/tep-grid/tep-tree-search.vue';
import tepCardsStore from '@/views/tep/tep-grid/store/tep-cards-store';

// Для компонента чекбокса
const validationResult = ref([]);
provide('validationResult', validationResult);
const userData = ref({});
provide('userData', userData);

// region variables
const treeViewMeta = {
    keyExpr: 'kod',
    displayExpr: 'name',
    parentIdExpr: 'kod_r',
    dataStructure: 'plain',
    rootValue: null,
    selectByClick: true,
    selectionMode: 'single',
    itemTemplate: 'item',
    expandAll: false,
    hideSearch: true,
    virtualModeEnabled: true,
    height: `${window.innerHeight -220}px`,
    hasItemsExpr: 'has_items'
};

const treeViewInstance = ref();
const treeViewRef = ref(null);
const checkBoxKodValue = ref(false);
const checkBoxMoveValue = ref(false);
const isContentReady = ref(true);
const isExpandAllChildren = ref(false);
const expandKod = ref(null);
const expandedChildren = ref([]);
const copiedItem = ref(null);
const loadingTreeItemList = ref([]);
const isRefresh = ref(true);
let scrollElement = null;
const elOpenContextMenu = ref(null);

const metaCheckboxKod = {
    'title': 'Код',
    'fieldNameText': 'cod'
};

const dataSource = new DataSource({
    store: new CustomStore({
        key: treeViewMeta.keyExpr,
        load: async ({filter}) => {
            let itemKod = null;
            if (isExpandAllChildren.value) { // при раскрытии всех детей передаем только то, что загрузили
                const res = store.state.tepTreeList.filter(el => expandedChildren.value.includes(el.kod));
                expandedChildren.value = []
                return res;
            }
            else if (Array.isArray(filter) && filter.length > 0 && !isRefresh.value) {
                // когда идет раскрытие одного элемента, то отдаем информацию только по нему
                if (filter[0] === 'kod_r') {
                    itemKod = expandKod.value ?? filter[1]
                    await loadChildren(itemKod); // здесь грузим данные по детям, если их еще не было
                    expandKod.value = null;
                    return store.state.tepTreeList.filter(el => el.kod_r === itemKod)
                } else if (filter[0] === 'kod') {
                    return store.state.tepTreeList.filter(el => el.kod === filter[1])
                }
            }
            isRefresh.value = false; // при рефреше перезагружаем все дерево, которое было предварительно очищено
            return [...store.state.tepTreeList]
        },
        onLoaded: (result) => {
            // после загрузки элементов дерева раскрываем и закрываем элементы по необходимости (в зависимости от сохраненных данных)
            setTimeout(() => {
                treeViewInstance.value?.option('animationEnabled', false);
                result.forEach(item => toggleExpandItem(item.kod, item.isExpanded ?? false))
                setTimeout(() => treeViewInstance.value?.option('animationEnabled', true), 0);
            }, 0)
        },
    })
});

const emit = defineEmits(['treeItemClick', 'createItem', 'deleteItem', 'refreshCardData']);
// endregion

// region help
const updateTepTree = async (filter = null) => {
    if (!isExpandAllChildren.value) isContentReady.value = false;
    const scrollableContainer =  document.querySelector('.dx-scrollable-container');
    const scrollTop = scrollableContainer?.scrollTop || 0;

    if (filter) dataSource.filter(filter);
    dataSource.load();
    await nextTick(() => {
        const updatedContainer = document.querySelector('.dx-scrollable-container');
        updatedContainer.scrollTop = scrollTop
    });
    if (!isExpandAllChildren.value) isContentReady.value = true;
}

/** Переключает состояние эл-та дерева */
const toggleExpandItem = (itemKod, isExpanded) => {
    const itemInd = store.state.tepTreeList.findIndex(el => el.kod === itemKod);
    if (itemInd !== -1) {
        store.state.tepTreeList[itemInd].isExpanded = isExpanded;
        if (isExpanded) treeViewInstance.value?.expandItem(itemKod);
        else treeViewInstance.value?.collapseItem(itemKod);
    }
}

const toggleLoadingIcon = (items) => {
    for (const item of items) {
        const itemInd = loadingTreeItemList.value.findIndex(el => el === item.kod);
        if (itemInd === -1) {
            loadingTreeItemList.value.push(item.kod);
        } else {
            loadingTreeItemList.value.splice(itemInd, 1);
        }
    }
}

const loadChildren = async (parentKod) => {
    if (!store.state.tepTreeLoadedItems.includes(parentKod)) {
        await store.loadTepTreeData(parentKod);
        store.state.tepTreeLoadedItems.push(parentKod);
    }
}
// endregion

// region events
onMounted(async () => {
    // если была смена департамента во время работы приложения, НЕ первый вход
    if(store.state.department !== null && +getCookie('dep') !== store.state.department) {
        isRefresh.value = true;
        store.clearTreeData();
        await store.loadTepTreeData(0);
        if(tepCardsStore.state.selectedItem) tepCardsStore.getSelectedItem(null);
        store.setDepartment();
        dataSource.reload();

    }
    // Если тот же департамент при переходе по табам
    else if(store.state.department !== null && +getCookie('dep') === store.state.department) {
        isRefresh.value = true;
        await dataSource.reload();
    }
    else {
        store.setDepartment();
        await dataSource.load();
    }
    treeViewInstance.value = treeViewRef.value?.treeViewInstance;

    if (Object.keys(store.state.tepTreeContextMenu).length === 0) {
        isContentReady.value = false;
        await store.loadAllContextMenuItems();
    }
    isContentReady.value = true;

    await nextTick();

    scrollElement = treeViewRef.value?.$el?.querySelector('.dx-scrollable-container');
    if (scrollElement) {
        scrollElement.addEventListener('scroll', handleScroll)
    }
});

onBeforeUnmount(() => {
    if (scrollElement) {
        scrollElement.removeEventListener('scroll', handleScroll)
    }
})

const onItemClick = async (event) => {
    const data = event?.itemData ? event?.itemData : event;
    emit('treeItemClick', data);
}

const onItemCollapsed = async (event) => {
    toggleExpandItem(event.itemData.kod, false);
    const allChildren = store.getAllChildren(event.itemData.kod);
    allChildren.forEach(childKod => {
        toggleExpandItem(childKod, false);
    })
}

const onRefresh = async () => {
    isContentReady.value = false;
    isRefresh.value = true;
    store.clearTreeData();
    await store.loadTepTreeData(0);
    if(tepCardsStore.state.selectedItem) tepCardsStore.getSelectedItem(null);
    dataSource.reload();
    isContentReady.value = true;
    emit('refreshCardData');
}
// endregion

// region context menu events
const expandAllChildren = async (kod) => {
    isExpandAllChildren.value = true;
    // отображем кружок загрузки
    const loadingItems = [{kod}];
    toggleLoadingIcon(loadingItems);

    await store.loadTepTreeItemAllChildren(kod); // грузим данные с бэка
    treeViewInstance.value.option('animationEnabled', false); // выключаем анимацию, чтобы не тормозила отрисовка

    // проходимся по всем детям раскрываемого элемента
    const children = store.getAllChildren(kod);
    expandedChildren.value = children; // нужно для последующей загрузки данных в дерево
    for (const item of store.state.tepTreeList) {
        if (children.includes(item.kod) || item.kod === kod) {
            item.isExpanded = true; // задаем им свойство раскрытия
        }
    }
    // отображаем изменения
    await updateTepTree();
    toggleLoadingIcon(loadingItems);
    isExpandAllChildren.value = false;

    // делаем это, чтобы родитель тоже раскрылся
    await treeViewInstance.value.expandItem(kod)
    treeViewInstance.value.option('animationEnabled', true);
}

const onCreateItem = async (kod_r, kod_vid) => {
    emit('createItem', {kod_r, kod_vid});
}

const createItemTepTree = async(itemKod) => {
    isContentReady.value = false;
    let items = await treeViewRef.value?.treeViewInstance.option('items');
    let itemNew = store.state.tepTreeList.find(el => el.kod === itemKod);
    items = items.map(item => {
        if(itemNew.kod_r === item.kod) {
            item.has_items = true;
            item.isExpanded = true;
        }
        return item;
    })
    items = [...items, itemNew];
    await treeViewRef.value?.treeViewInstance.option('items', items);
    isContentReady.value = true;
}

const onMoveItem = async (kod, kod_r) => {
    isContentReady.value = false;
    await store.moveItem(kod, kod_r);
    await updateTepTree();
}

const onDeleteItem = async (kod, kod_r) => {
    isContentReady.value = false;
    await store.deleteTreeItem(kod, kod_r);
    let items = await treeViewRef.value?.treeViewInstance.option('items')
    items = items.filter(item => item.kod !== kod);
    await treeViewRef.value?.treeViewInstance.option('items', items);
    emit('deleteItem', kod)
    isContentReady.value = true;
}

const onOpenSearchItemTree = async (data, kod) => {

    isExpandAllChildren.value = true;
    // // отображем кружок загрузки
    const loadingItems = [{kod}];
    toggleLoadingIcon(loadingItems);

    treeViewInstance.value.option('animationEnabled', false); // выключаем анимацию, чтобы не тормозила отрисовка

    // проходимся по всем детям раскрываемого элемента
    const children = store.getAllChildren(kod);
    expandedChildren.value = children; // нужно для последующей загрузки данных в дерево
     for (const item of store.state.tepTreeList) {
        if (children.includes(item.kod) || item.kod === kod) {
            if(store.state.allParentsId.includes(item.kod)) {
                item.isExpanded = true; // задаем им свойство раскрытия
            }
       }
    }
    // отображаем изменения
    await updateTepTree();
    toggleLoadingIcon(loadingItems);
    isExpandAllChildren.value = false;

    // делаем это, чтобы родитель тоже раскрылся
    await treeViewInstance.value.expandItem(kod)
    treeViewInstance.value.option('animationEnabled', true);

    await nextTick();

    requestAnimationFrame(() => {
        const itemData = { ...data, selected: true };
        onItemClick(itemData);
        treeViewRef?.value?.treeViewInstance?.scrollToItem(data.kod);
        treeViewRef?.value?.treeViewInstance?.selectItem(data.kod);
    });
}

const changeCheckboxKod = () => {
    checkBoxKodValue.value = !checkBoxKodValue.value;
}

const scrollToItem = async(item) => {
    const kod = item.kod;
    await nextTick();
    await treeViewRef?.value?.treeViewInstance?.scrollToItem(kod);
}

const debounce = (fn, delay) => {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), delay);
    };
}

const handleScroll = debounce(() => {
    if(elOpenContextMenu.value) {
        elOpenContextMenu.value.hide();
    }
},10);

const onGetOpenEl = (el) => {
    elOpenContextMenu.value = el;
}

// const changeCheckboxMove = (_) => {
//     checkBoxMoveValue.value = !checkBoxMoveValue.value;
//     copiedItem.value = null;
// }
// endregion

defineExpose({
    updateTepTree,
    createItemTepTree,
    scrollToItem,
})
</script>

<style scoped>
#tep-tree-view-container {
    overflow-x: auto;
}
#tep-tree-view-container :deep(.flex.bottom-tree-view-container.items-center) {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: sticky;
    top: 0;
    z-index: 1;
    background: #F6FAFE;
}
.tep-tree-view-toolbar {
    width: 100%;
    padding: 10px;
    display: flex;
    align-items: center;
}
.tep-tree-view-item {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
}
.tep-tree-item-img {
    width: auto;
    height: 1.5em;
    margin-right: 10px;
}
.tep-tree-view-toolbar-item {
    margin-right: 10px;
}

.tep-tree-view-toolbar-item :deep(.dx-field-left) {
    flex-direction: row-reverse;
    justify-content: flex-start;
    padding-left: 20px;
}
.tep-tree-view-toolbar-item :deep(.dx-field-label) {
    display: flex;
    flex: 0 0 100%;
    font-weight: 400;
    font-size: 12px;
    padding-left: 8px;
    float: none;
    padding-right: 0;
}

.tep-tree-view-toolbar-item :deep(.dx-field-value-left) {
    max-width: 28px;
    display: flex;
    justify-content: flex-start;
}

.tep-tree-view-toolbar-item :deep(.dx-button) {
    background-color: transparent;
}
</style>
